import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDisableCandidateFormActions } from 'contexts/contexts';
import {
  getDependentsFromValues,
  runHiddenDependents,
} from 'helpers/candidate_application/dependent-field-processing';
import { hasAnyChange } from 'helpers/compare-objects';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';
import {
  Address,
  Checkbox,
  DateSelector,
  DocumentUpload,
  Email,
  MultipleChoice,
  Select,
  Text,
  Textarea,
} from 'components/FormComponents';
import { CustomToast } from 'components/Toast/CustomToast';

import {
  saveCandidateChecksValues,
  selectCandidateApplication,
  updateCandidateValues,
  updateHiddenValues,
  updateIdUploadDetail,
} from 'api/candidate-application-slice';

export const ExtractedFieldsDisplay = ({
  displayFields = [],
  idValues = {},
  brand,
  uploadId = null,
  confirmedByCand = false,
  onConfirm = () => {},
}) => {
  const dispatch = useDispatch();
  const {
    candidateFields: {
      candidateValues,
      hiddenValues,
      hiddenDependencyCodes = {},
      hiddenFields = {},
    },
  } = useSelector(selectCandidateApplication);
  const { token } = useParams();
  const { registerPendingConfirmation, unregisterPendingConfirmation } =
    useDisableCandidateFormActions();

  // Generate a unique identifier for this confirmation instance
  // Use uploadId if available, otherwise generate a unique one
  const confirmationIdRef = useRef(
    uploadId ? `upload-${uploadId}` : `extracted-${Date.now()}-${Math.random()}`,
  );

  // Update confirmation ID when uploadId changes
  useEffect(() => {
    const oldId = confirmationIdRef.current;
    const newId = uploadId ? `upload-${uploadId}` : `extracted-${Date.now()}-${Math.random()}`;

    // If the ID changed, unregister the old one, update the ref, and register the new one
    if (oldId !== newId) {
      unregisterPendingConfirmation(oldId);
      confirmationIdRef.current = newId;
      // Register the new ID if we have displayFields (component is active)
      if (displayFields?.length > 0) {
        registerPendingConfirmation(newId);
      }
    }
  }, [uploadId, unregisterPendingConfirmation, registerPendingConfirmation, displayFields?.length]);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {},
  });

  const didInitRef = useRef(false);

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  const handlers = useMemo(
    () => ({
      getCandidateUpload: getUpload,
      signCandidateUpload: signUpload,
      createCandidateUpload: createUpload,
      deleteCandidateUpload: deleteUpload,
    }),
    [getUpload, signUpload, createUpload, deleteUpload],
  );

  // Watch all form values
  const fieldValues = watch();

  // Create merged getValues that includes candidateValues for validators to access (e.g., dob)
  const getMergedValues = useCallback(
    () => ({
      ...candidateValues,
      ...hiddenValues,
      ...watch(), // local form values take precedence
    }),
    [candidateValues, hiddenValues, watch],
  );

  // State to track idUploadDetailsField mappings for each field
  const [idUploadDetailMappings, setIdUploadDetailMappings] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(confirmedByCand);
  const previousValuesRef = useRef(fieldValues);

  // Reset confirmation status when form values change
  useEffect(() => {
    if (isConfirmed) {
      const hasChanged = JSON.stringify(previousValuesRef.current) !== JSON.stringify(fieldValues);
      if (hasChanged) {
        setIsConfirmed(false);
      }
    }
    previousValuesRef.current = fieldValues;
  }, [fieldValues, isConfirmed]);

  // Helper function to determine if component needs controlled input
  const getComponentProps = useCallback(
    (value, question) => {
      // Components that need Controller (controlledInput: true)
      const controlledComponents = [
        'select',
        'multiple_choice',
        'date_selector',
        'upload',
        'checkbox',
      ];

      return {
        controlledInput: controlledComponents.includes(question?.type),
        children:
          question?.options?.map(option => (
            <Select.Item key={option.value} value={option.value}>
              {option.name || option.label}
            </Select.Item>
          )) || [],
        defaultValue: question?.defaultValue,
        getUpload: handlers.getCandidateUpload,
        signUpload: handlers.signCandidateUpload,
        createUpload: handlers.createCandidateUpload,
        deleteUpload: handlers.deleteCandidateUpload,
        noErrorMessage: true,
      };
    },
    [handlers],
  );

  // Memoize component map to prevent recreation on every render
  const localComponentMap = useMemo(
    () => ({
      short_text: Text,
      long_text: Textarea,
      select: Select,
      date_selector: DateSelector,
      multiple_choice: MultipleChoice,
      checkbox: Checkbox,
      email: Email,
      address: Address,
      upload: DocumentUpload,
    }),
    [],
  );

  // Field value mapping configuration
  const fieldValueMappings = useMemo(
    () => ({
      passport_nationality: 'passport_nationality_alpha2',
      // Add other field mappings here as needed
    }),
    [],
  );

  // Memoize helper function to prevent recreation on every render
  const getFieldValue = useCallback(
    (fieldCode, baseCode) => {
      // Combine candidateValues and hiddenValues (user-updated values)
      // Don't include idValues here to avoid overriding user updates
      const allValues = {
        ...candidateValues,
        ...hiddenValues,
      };

      // Check if there's a specific mapping for this field
      const mappedField = fieldValueMappings[fieldCode];

      // Check allValues first (user updates), then fall back to idValues (extracted values)
      // Check mappedField first if it exists, then fieldCode, then baseCode
      if (mappedField) {
        if (mappedField in allValues && allValues[mappedField] !== '') {
          return allValues[mappedField];
        }
        if (mappedField in idValues && idValues[mappedField] !== '') {
          return idValues[mappedField];
        }
      }

      // Check fieldCode and baseCode in allValues first, then idValues
      if (fieldCode in allValues && allValues[fieldCode] !== '') {
        return allValues[fieldCode];
      }
      if (baseCode in allValues && allValues[baseCode] !== '') {
        return allValues[baseCode];
      }
      if (baseCode in idValues && idValues[baseCode] !== '') {
        return idValues[baseCode];
      }
      if (fieldCode in idValues && idValues[fieldCode] !== '') {
        return idValues[fieldCode];
      }

      return '';
    },
    [idValues, fieldValueMappings, candidateValues, hiddenValues],
  );

  // Evaluate field conditions using current group values (idValues) and form values
  const evaluateFieldVisibility = useCallback(
    (field, groupValues, formValues = {}) => {
      if (!field.conditions) return true;

      // Combine candidateValues, hiddenValues, current idValues, and form values for condition evaluation
      const allValues = {
        ...candidateValues,
        ...hiddenValues,
        ...groupValues,
        ...formValues, // Include current form values from React Hook Form
      };

      return evaluateFieldConditions(field.conditions, allValues, {});
    },
    [candidateValues, hiddenValues],
  );

  // Register/unregister pending confirmation when ExtractedFieldsDisplay is visible
  // Use isConfirmed state (not just confirmedByCand prop) so that when user changes values
  // after confirming, we re-register the pending confirmation
  useEffect(() => {
    if (displayFields?.length > 0 && !isConfirmed) {
      // Register this instance as needing confirmation
      registerPendingConfirmation(confirmationIdRef.current);
    } else {
      // Unregister if fields are no longer displayed or already confirmed
      unregisterPendingConfirmation(confirmationIdRef.current);
    }

    // Cleanup: unregister when component unmounts
    return () => {
      unregisterPendingConfirmation(confirmationIdRef.current);
    };
  }, [displayFields, isConfirmed, registerPendingConfirmation, unregisterPendingConfirmation]);

  // Initialize form with extracted data when idValues change
  useEffect(() => {
    // Only run if we have both idValues data AND displayFields
    if (didInitRef.current) {
      return;
    }
    if (displayFields?.length > 0) {
      didInitRef.current = true;
      const initialValues = {};

      const processFields = fields => {
        for (const field of fields || []) {
          if (field.code) {
            const value = getFieldValue(field.code, field.baseCode);
            if (value !== '') {
              initialValues[field.code] = value;
            }
          }
          if (field.subFields) {
            processFields(field.subFields);
          }
          const altIdGroups = field.altIdFields || field.alt_id_fields;
          if (Array.isArray(altIdGroups)) {
            altIdGroups.forEach(group => processFields(group.subFields || group.sub_fields || []));
          }
        }
      };

      processFields(displayFields);
      reset(initialValues);
    }
  }, [displayFields, idValues, reset, getFieldValue]);

  // Build idUploadDetailsField mappings when displayFields change
  useEffect(() => {
    const mappings = {};

    const processFields = fields => {
      for (const field of fields || []) {
        if (field.idUploadDetailsField) {
          mappings[field.code] = field.idUploadDetailsField;
        }
        if (field.subFields) {
          processFields(field.subFields);
        }
        const altIdGroups = field.altIdFields || field.alt_id_fields;
        if (Array.isArray(altIdGroups)) {
          altIdGroups.forEach(group => processFields(group.subFields || group.sub_fields || []));
        }
      }
    };

    processFields(displayFields);
    setIdUploadDetailMappings(mappings);
  }, [displayFields]);

  const mapIdValuesToBaseCodes = (displayFields, idValues) => {
    if (!Array.isArray(displayFields) || !displayFields.length || !idValues) return {};

    const subFieldsToEntries = subs =>
      (subs || []).map(sub => [sub?.code, sub?.baseCode]).filter(([code, base]) => code && base);
    const codeToBaseMap = Object.fromEntries(
      displayFields.flatMap(field => {
        const fromSubFields = Array.isArray(field?.subFields)
          ? subFieldsToEntries(field.subFields)
          : [];
        const fromAltId = (field?.altIdFields || field?.alt_id_fields || []).flatMap(group =>
          subFieldsToEntries(group?.subFields || group?.sub_fields),
        );
        return [...fromSubFields, ...fromAltId];
      }),
    );

    return Object.fromEntries(
      Object.entries(idValues).map(([key, value]) => [codeToBaseMap[key] || key, value ?? null]),
    );
  };

  const handleConfirmDetails = useCallback(
    async data => {
      try {
        setIsSubmitting(true);
        // Process hidden dependencies before saving
        const allValues = {
          ...candidateValues,
          ...hiddenValues,
          ...data, // Include the form data being submitted
        };

        const hiddenDeps = getDependentsFromValues(allValues, hiddenDependencyCodes, {
          includeIndexes: false, // hidden fields don't have indexes
          ignoreEmpty: true,
        });

        if (hiddenDeps.length > 0) {
          const hiddenPatch = runHiddenDependents(hiddenDeps, allValues, hiddenFields);
          if (hiddenPatch) {
            dispatch(updateHiddenValues(hiddenPatch));
          }
        }

        // First, try to save to id_upload_detail if we have an uploadId
        if (uploadId) {
          // Map extracted fields to id_upload_detail fields using pre-built mappings
          const idUploadDetailFields = {};
          Object.entries(data).forEach(([fieldCode, value]) => {
            const idUploadDetailsField = idUploadDetailMappings[fieldCode];
            if (idUploadDetailsField) {
              idUploadDetailFields[idUploadDetailsField] = value;
            }
          });

          const mappedIdValues = mapIdValuesToBaseCodes(displayFields, data);
          const { changed } = hasAnyChange(idValues, mappedIdValues);
          // Update id_upload_detail with confirmed_by_cand flag
          try {
            await dispatch(
              updateIdUploadDetail({
                token,
                uploadId,
                data: {
                  ...idUploadDetailFields,
                  cand_fields_overridden: changed,
                  confirmed_by_cand: true,
                },
              }),
            ).unwrap(); // This will throw if the action is rejected
          } catch (error) {
            console.error('Error updating id_upload_detail:', error);
          }
        }

        // Merge candidateValues and data first
        let updatedCandidateValues = {
          ...candidateValues,
          ...data,
        };

        // Check visibility for all displayFields and remove values if conditions are not met
        const processFieldsForVisibility = fields => {
          for (const field of fields || []) {
            if (field.code) {
              // Check if field should be visible using idValues and form data
              const isVisible = evaluateFieldVisibility(field, idValues, data);

              // If field is not visible, remove its value from updatedCandidateValues
              if (!isVisible && field.code in updatedCandidateValues) {
                delete updatedCandidateValues[field.code];
              }
            }

            if (field.subFields) {
              processFieldsForVisibility(field.subFields);
            }
            const altIdGroups = field.altIdFields || field.alt_id_fields;
            if (Array.isArray(altIdGroups)) {
              altIdGroups.forEach(group =>
                processFieldsForVisibility(group.subFields || group.sub_fields || []),
              );
            }
          }
        };

        processFieldsForVisibility(displayFields);

        // If id_upload_detail save succeeded (or no uploadId), proceed with candidateValues
        // Update local state - save as sectionless values
        dispatch(
          updateCandidateValues({
            updatedCandidateValues,
          }),
        );

        // Also save to backend
        dispatch(
          saveCandidateChecksValues({
            token,
            data: {
              values: updatedCandidateValues,
            },
          }),
        );

        toast.custom(
          t => (
            <CustomToast
              t={t}
              title="Thank you for confirming your details!"
              message="Your details have been saved successfully."
              type="success"
            />
          ),
          {
            duration: 5000,
          },
        );
        setIsConfirmed(true);
        onConfirm();
        previousValuesRef.current = data;
      } catch (error) {
        toast.custom(
          t => (
            <CustomToast
              t={t}
              title="Oops - something went wrong"
              message={
                error?.message ||
                error ||
                'Please try again or proceed to the next step. You can email us at team@checkmate.tech if you need help.'
              }
              type="error"
            />
          ),
          {
            duration: 5000,
          },
        );
      } finally {
        // Unregister this confirmation after successful confirmation
        unregisterPendingConfirmation(confirmationIdRef.current);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      }
    },
    [
      dispatch,
      token,
      candidateValues,
      uploadId,
      idUploadDetailMappings,
      hiddenValues,
      idValues,
      hiddenDependencyCodes,
      hiddenFields,
      unregisterPendingConfirmation,
    ],
  );

  // Memoize the main render logic
  const renderedFields = useMemo(() => {
    if (!displayFields || displayFields?.length === 0) {
      return null;
    }

    return (displayFields || [])?.map((field, index) => {
      // Handle nested structure - if field has subFields, render them
      if (field.subFields && field.subFields.length > 0) {
        return (
          <div key={`field_${index}`} className="d-flex flex-column u-gap-large">
            <span className="primary-text u-font-size-large u-font-weight-500">
              {field.title || field.name}
            </span>
            {(field?.subFields || [])?.map((subField, subIndex) => {
              // Check if subfield should be visible using current group values and form values
              const isVisible = evaluateFieldVisibility(subField, idValues, fieldValues);
              if (!isVisible) return null;

              const question = {
                id: subField.code || subField.id || `sub_field_${index}_${subIndex}`,
                type: subField.type || 'text',
                text: subField.title || '',
                description: subField.description || '',
                required: subField.required,
                ...subField,
              };

              const fieldKey = subField.code || subField.name || subField.id;

              return (
                <div key={question.id} className="d-flex flex-column u-gap-small">
                  <QuestionWrapper
                    question={question}
                    children={question.children}
                    fieldName={fieldKey}
                    control={control}
                    register={register}
                    setValue={setValue}
                    trigger={trigger}
                    getValues={getMergedValues}
                    componentMap={localComponentMap}
                    requirementVariantStyle="text"
                    gap="small"
                    titleSize="small"
                    getComponentProps={value => getComponentProps(value, question)}
                    allValues={fieldValues}
                    error={errors[fieldKey]?.message || ''}
                    isVisible={true}
                  />
                </div>
              );
            })}
          </div>
        );
      }

      // Handle regular fields
      // Check if field should be visible using current group values and form values
      const isVisible = evaluateFieldVisibility(field, idValues, fieldValues);
      if (!isVisible) return null;

      const question = {
        id: field.code || field.id || field.name || `display_field_${index}`,
        type: field.type || 'text',
        text: field.label || field.name || field.code || `Field ${index + 1}`,
        description: field.description || '',
        required: Boolean(field.required),
        ...field,
      };

      const fieldKey = field.code || field.name || field.id;

      return (
        <div key={question.id} className="d-flex flex-column u-gap-small">
          <QuestionWrapper
            question={question}
            fieldName={fieldKey}
            control={control}
            register={register}
            setValue={setValue}
            trigger={trigger}
            getValues={getMergedValues}
            componentMap={localComponentMap}
            getComponentProps={value => getComponentProps(value, question)}
            allValues={fieldValues}
            requirementVariantStyle="text"
            gap="small"
            titleSize="small"
            error={errors[fieldKey]?.message || ''}
            isVisible={true}
            {...(question.type === 'select' &&
              field.options && {
                children: field.options.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.name || option.label}
                  </Select.Item>
                )),
              })}
          />
        </div>
      );
    });
  }, [
    displayFields,
    idValues,
    getFieldValue,
    localComponentMap,
    control,
    register,
    setValue,
    trigger,
    getMergedValues,
    getComponentProps,
    fieldValues,
    errors,
    brand,
    evaluateFieldVisibility,
  ]);

  if (!renderedFields) {
    return null;
  }

  return (
    <div className="d-flex flex-column u-gap-medium">
      {renderedFields}
      <CustomButton
        onClick={handleSubmit(handleConfirmDetails)}
        brand={brand}
        className="u-margin-top--small"
        disabled={isConfirmed || isSubmitting}
        aria-label="Confirm Details">
        {isSubmitting ? 'Confirming...' : 'Confirm Details'}
      </CustomButton>
    </div>
  );
};

ExtractedFieldsDisplay.propTypes = {
  displayFields: PropTypes.array,
  idValues: PropTypes.object,
  brand: PropTypes.object,
  uploadId: PropTypes.number,
  confirmedByCand: PropTypes.bool,
};
